import type { ConversationMessage } from "@/stores/conversation-store";
import { normalizeAssistantText } from "./assistant-text-normalization";

export function normalizeConversationMessage(
  msg: ConversationMessage,
): ConversationMessage {
  if (msg.role !== "assistant") return msg;
  return { ...msg, content: normalizeAssistantText(msg.content) };
}

export function appendConversationMessages(
  state: { messages: ConversationMessage[] },
  msgs: ConversationMessage[],
): { messages: ConversationMessage[] } | typeof state {
  if (msgs.length === 0) return state;
  const existingIds = new Set(state.messages.map((message) => message.id));
  const nextMessages: ConversationMessage[] = [];
  for (const msg of msgs) {
    if (existingIds.has(msg.id)) continue;
    existingIds.add(msg.id);
    nextMessages.push(normalizeConversationMessage(msg));
  }
  if (nextMessages.length === 0) return state;
  return { messages: [...state.messages, ...nextMessages] };
}

export function upsertDurableMessage(
  state: { messages: ConversationMessage[] },
  message: ConversationMessage,
): { messages: ConversationMessage[] } {
  const index = state.messages.findIndex(
    (existing) => existing.id === message.id,
  );
  if (index < 0)
    return {
      messages: [...state.messages, normalizeConversationMessage(message)],
    };
  const existing = state.messages[index]!;
  // A replayed partial must never replace a completed durable row.
  if (existing.nativeFinal === true && message.nativeFinal === false)
    return state;
  const messages = [...state.messages];
  messages[index] = normalizeConversationMessage({ ...existing, ...message });
  return { messages };
}

export function settleExecutionMessages(
  state: { messages: ConversationMessage[] },
  executionId: string,
) {
  return {
    messages: state.messages.flatMap((message) => {
      if (!message.isStreaming || message.executionId !== executionId)
        return [message];
      return message.nativeSubagentId
        ? [{ ...message, isStreaming: false }]
        : [];
    }),
  };
}
